import { endOfDay, startOfDay } from 'date-fns';
import {
  createDailyEventsFromExternalEvents,
  mapExternalEventToFullCalendarEvent,
  useTodayExternalCalendarEvents,
} from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useMemo } from 'react';
import { useUser } from 'shared/hooks/use-user';
import {
  getGoalsDeadlineEvents,
  getTasksEventsForDateRange,
} from 'shared/lib/full-calendar';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

export const useDayEvents = (day: Timestamp, tasks: Task[]) => {
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const goals = useGoals();
  const externalEvents = useTodayExternalCalendarEvents();

  const taskEvents = useMemo(
    () =>
      getTasksEventsForDateRange(
        user?.settings?.calendar?.hideHabits
          ? tasks.filter(({ schedules }) => !schedules?.length)
          : tasks,
        {
          startDate: startOfDay(day),
          endDate: endOfDay(day),
          weekStartsOn,
          hideCompleted: user?.settings?.calendar?.hideCompletedTasks,
        },
      ),
    [
      user?.settings?.calendar?.hideCompletedTasks,
      user?.settings?.calendar?.hideHabits,
      tasks,
      day,
      weekStartsOn,
    ],
  );

  const goalEvents = useMemo(() => getGoalsDeadlineEvents(goals), [goals]);

  const externalFullCalendarEvents = useMemo(
    () =>
      createDailyEventsFromExternalEvents(externalEvents).map((externalEvent) =>
        mapExternalEventToFullCalendarEvent(externalEvent),
      ),
    [externalEvents],
  );

  const events = useMemo(
    () => [
      ...(user?.settings?.calendar?.hideExternalCalendarEvents
        ? []
        : externalFullCalendarEvents),
      ...taskEvents,
      ...goalEvents,
    ],
    [
      externalFullCalendarEvents,
      goalEvents,
      taskEvents,
      user?.settings?.calendar?.hideExternalCalendarEvents,
    ],
  );

  return {
    events,
    tasks,
  };
};
